import { Controller } from "@hotwired/stimulus"

const KEY = "pd_cookies_consent"
const MAX_AGE = 60 * 60 * 24 * 365 // 1 year
const OPEN_EVENT = "pd:cookies:open"

export default class extends Controller {
  static targets = ["banner", "modal", "analyticsToggle", "marketingToggle"]

  connect() {
    this.openHandler = () => this.openSettings()
    window.addEventListener(OPEN_EVENT, this.openHandler)

    const consent = this.#read()
    if (consent) {
      this.#hydrateToggles(consent)
      return
    }
    this.bannerTarget.hidden = false
    requestAnimationFrame(() => this.bannerTarget.classList.add("is-visible"))
  }

  disconnect() {
    window.removeEventListener(OPEN_EVENT, this.openHandler)
  }

  acceptAll(event) {
    event?.preventDefault()
    this.#store({ analytics: true, marketing: true })
    this.#hydrateToggles({ analytics: true, marketing: true })
    this.#closeModal()
    this.#dismiss()
  }

  rejectAll(event) {
    event?.preventDefault()
    this.#store({ analytics: false, marketing: false })
    this.#hydrateToggles({ analytics: false, marketing: false })
    this.#dismiss()
  }

  openSettings(event) {
    event?.preventDefault()
    if (!this.hasModalTarget) return
    if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    } else {
      this.modalTarget.setAttribute("open", "")
    }
  }

  closeSettings(event) {
    event?.preventDefault()
    this.#closeModal()
  }

  save(event) {
    event.preventDefault()
    this.#store({
      analytics: this.hasAnalyticsToggleTarget && this.analyticsToggleTarget.checked,
      marketing: this.hasMarketingToggleTarget && this.marketingToggleTarget.checked,
    })
    this.#closeModal()
    this.#dismiss()
  }

  #closeModal() {
    if (!this.hasModalTarget) return
    if (typeof this.modalTarget.close === "function") {
      this.modalTarget.close()
    } else {
      this.modalTarget.removeAttribute("open")
    }
  }

  #dismiss() {
    if (!this.hasBannerTarget || this.bannerTarget.hidden) return
    this.bannerTarget.classList.remove("is-visible")
    setTimeout(() => { this.bannerTarget.hidden = true }, 250)
  }

  #hydrateToggles(consent) {
    if (this.hasAnalyticsToggleTarget) this.analyticsToggleTarget.checked = !!consent.analytics
    if (this.hasMarketingToggleTarget) this.marketingToggleTarget.checked = !!consent.marketing
  }

  #store(prefs) {
    const payload = {
      v: 1,
      essential: true,
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing,
      ts: new Date().toISOString().slice(0, 10),
    }
    const value = encodeURIComponent(JSON.stringify(payload))
    document.cookie = `${KEY}=${value}; max-age=${MAX_AGE}; path=/; SameSite=Lax`
  }

  #read() {
    const raw = document.cookie.split("; ").find((c) => c.startsWith(`${KEY}=`))
    if (!raw) return null
    try {
      return JSON.parse(decodeURIComponent(raw.slice(KEY.length + 1)))
    } catch {
      return null
    }
  }
}
